import React, { useEffect } from 'react';
import { useSalleQuestions } from '../../../hooks/useSalle';

/**
 * LES QUESTIONS DE LA SALLE, DANS LE CONDUCTEUR, à la slide 34.
 *
 * Posé par le marqueur `[[QUESTIONS]]` écrit dans le conducteur, comme
 * `[[SYNTHÈSE]]` pose son bouton (voir `RenduConducteur`). Jusqu'ici, les
 * questions ne se lisaient que dans le panneau plein écran du compteur : il
 * fallait le penser, l'ouvrir, puis le refermer pour retrouver le conducteur.
 * À ce moment-là de la conférence, Adrien a fini de parler et c'est la seule
 * chose qui reste à l'écran — autant qu'elle y soit déjà.
 *
 * ⚠️ RIEN D'AUTRE NE DOIT BOUGER QUAND LA SYNTHÈSE ARRIVE, et c'est la
 * contrainte qui a dicté le montage. Adrien lit le conducteur pendant que
 * l'analyse tourne : un prompteur qui se recompose lui fait perdre sa ligne au
 * milieu d'une phrase. Ce composant détient donc SA propre lecture et se
 * redessine seul. L'état ne remonte à aucun parent, et le conducteur n'est ni
 * re-découpé ni re-rendu.
 *
 * Il ne bat pas non plus : il lit une fois au montage, puis sur l'événement
 * `salle:pret` qu'émet le compteur à la fin d'une génération (`CompteurSalle`).
 * C'est le pendant de `salle:synthese`, en sens inverse.
 *
 * LES 3 QUESTIONS EN COULEUR sont celles que le modèle a retenues comme
 * traitables à voix haute (`saillantes`, voir `prompts/synthese_salle.md`).
 * Elles restent À LEUR PLACE dans leur thème, jamais regroupées en tête : ce
 * qu'Adrien doit voir, c'est laquelle prendre dans le thème qu'il est en train
 * d'annoncer, pas une quatrième liste à recroiser.
 *
 * LES REGROUPEMENTS (22/09) — « ×3 Quel outil choisir », et les 3 questions
 * d'origine dessous. Deux raisons, et la seconde compte plus que la première :
 *
 *  1. le « ×3 » dit à Adrien ce qui revient, donc par quoi commencer ;
 *  2. **c'est exactement ce que la slide 35 projette.** Les libellés sont
 *     écrits par le modèle, pas par lui ; les lui montrer ICI, à la slide 34,
 *     est ce qui fait qu'il les a relus avant qu'ils arrivent sur le mur. Voir
 *     `SalleQuestionsSlide.jsx`. Ne pas retirer cet affichage sans retirer
 *     aussi la slide 35.
 *
 * Le thème garde ses questions non regroupées à la suite : un classement
 * déposé avant les regroupements, ou un modèle qui ne les rend pas, doit
 * continuer à s'afficher comme avant.
 *
 * Thème CLAIR (tout le prompteur l'est).
 */
const QuestionsDeLaSalle = () => {
    const { classement, chargement, relire } = useSalleQuestions({ actif: true });

    useEffect(() => {
        const surPret = () => relire();
        window.addEventListener('salle:pret', surPret);
        return () => window.removeEventListener('salle:pret', surPret);
    }, [relire]);

    const groupes = [
        ...(classement?.themes || []).map((t) => ({
            titre: t.titre || 'Sans titre', questions: t.questions, ton: 'sky',
            regroupements: t.regroupements || [],
        })),
        { titre: 'Sans thème', questions: classement?.sans_theme || [], ton: 'slate' },
        // Arrivées après la synthèse : la salle ne les a pas entendues dans le
        // texte lu. À prendre au vol, donc à distinguer.
        { titre: 'Arrivées depuis la synthèse', questions: classement?.depuis || [], ton: 'amber' },
        { titre: 'Écartées', questions: classement?.ecartees || [], ton: 'slate' },
    ].filter((g) => g.questions.length > 0);

    if (chargement && !classement) {
        return <span className="block my-[0.5em] text-slate-400">Lecture des questions…</span>;
    }

    if (groupes.length === 0) {
        return (
            <span className="block my-[0.5em] text-slate-500">
                Aucune question classée — lancer l'analyse (⟳) à la slide 31.
            </span>
        );
    }

    /* Une question, telle qu'Adrien la lit à voix haute. */
    const ligne = (q) => (
        <span
            key={q.n}
            className={`flex gap-[0.6em] items-baseline leading-snug ${q.saillante
                /* La mise en avant se voit à trois mètres : fond, bord gauche
                   et gras. La couleur seule ne suffit pas sous un
                   vidéoprojecteur, ni pour qui la distingue mal. */
                ? 'text-violet-900 font-semibold bg-violet-50 border-l-4 border-violet-500 pl-[0.5em] py-[0.15em] rounded-r'
                : 'text-slate-800'}`}
        >
            <span className="font-mono text-[0.75em] text-slate-400 tabular-nums shrink-0">
                {q.n}.
            </span>
            <span>{q.texte}</span>
        </span>
    );

    return (
        /* `block` sur un span : le marqueur est rendu DANS un paragraphe du
           conducteur, et un <div> y serait invalide en HTML. */
        <span className="block my-[0.6em] space-y-[0.8em]">
            {groupes.map((g) => {
                // Ce que les regroupements couvrent déjà : le reste du thème
                // se liste à la suite, sans libellé.
                const couverts = new Set(
                    (g.regroupements || []).flatMap((r) => (r.questions || []).map((q) => q.n)),
                );
                const restantes = g.questions.filter((q) => !couverts.has(q.n));

                return (
                    <span key={g.titre} className="block">
                        <span className={`block text-[0.62em] font-bold uppercase tracking-wider mb-[0.25em] ${g.ton === 'sky' ? 'text-sky-800'
                            : g.ton === 'amber' ? 'text-amber-800' : 'text-slate-400'}`}>
                            {g.titre}
                            <span className="ml-2 font-mono tabular-nums opacity-60">{g.questions.length}</span>
                        </span>

                        <span className="block space-y-[0.45em]">
                            {(g.regroupements || []).map((r, i) => (
                                <span key={`r-${i}`} className="block">
                                    {/* Le libellé projeté en slide 35, mot pour mot. */}
                                    <span className="block text-[0.78em] text-slate-500 italic leading-snug">
                                        {r.libelle}
                                    </span>
                                    <span className="block space-y-[0.2em] pl-[0.9em] border-l border-slate-200">
                                        {(r.questions || []).map(ligne)}
                                    </span>
                                </span>
                            ))}

                            {restantes.length > 0 && (
                                <span className="block space-y-[0.2em]">{restantes.map(ligne)}</span>
                            )}
                        </span>
                    </span>
                );
            })}
        </span>
    );
};

export default QuestionsDeLaSalle;
